import { supabase } from '../lib/supabase';
import { ApiError } from '../lib/errors';
import { isDuplicateCandidate, isDuplicateJob } from '../lib/utils';

interface DashboardStats {
  totalAnalyses: number;
  uniqueCandidates: number;
  uniqueJobs: number;
  recentAnalyses: number;
  totalCandidates: number;
  totalJobs: number;
}

// Helper function to get user's organization ID
async function getUserOrganizationId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: member } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id)
      .maybeSingle();

    return member?.organization_id || null;
  } catch (error) {
    console.error('Error getting user organization:', error);
    return null;
  }
}

export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new ApiError('User not authenticated', 401);

    // Get user's organization ID
    const organizationId = await getUserOrganizationId();
    if (!organizationId) {
      // Return empty stats if user has no organization yet
      return {
        totalAnalyses: 0,
        uniqueCandidates: 0,
        uniqueJobs: 0,
        recentAnalyses: 0,
        totalCandidates: 0,
        totalJobs: 0
      };
    }

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const [
      { count: totalAnalyses },
      { data: candidates },
      { data: jobs },
      { count: recentAnalyses }
    ] = await Promise.all([
      supabase.from('analysis')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId),
      supabase.from('candidates')
        .select('id, name, cv_text')
        .eq('organization_id', organizationId),
      supabase.from('jobs')
        .select('id, title, jd_text')
        .eq('organization_id', organizationId),
      supabase.from('analysis')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .gte('created_at', oneWeekAgo.toISOString())
    ]);

    // Count unique candidates
    const uniqueCandidates = candidates?.reduce<Array<typeof candidates[0]>>((acc, current) => {
      const isDuplicate = acc.some(c => 
        isDuplicateCandidate(current.name, current.cv_text, [c])
      );
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []) || [];

    // Count unique jobs
    const uniqueJobs = jobs?.reduce<Array<typeof jobs[0]>>((acc, current) => {
      const isDuplicate = acc.some(j => 
        isDuplicateJob(current.title, current.jd_text, [j])
      );
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []) || [];

    return {
      totalAnalyses: totalAnalyses || 0,
      uniqueCandidates: uniqueCandidates.length,
      uniqueJobs: uniqueJobs.length,
      recentAnalyses: recentAnalyses || 0,
      totalCandidates: uniqueCandidates.length,
      totalJobs: uniqueJobs.length
    };
  } catch (error) {
    console.error('Dashboard stats error:', error);
    throw new ApiError('Failed to fetch dashboard stats', 500);
  }
}

export async function getProjectDashboardStats(projectId: string): Promise<DashboardStats> {
  try {
    console.log('🔍 Getting project dashboard stats for project:', projectId);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new ApiError('User not authenticated', 401);

    // Get user's organization ID
    const organizationId = await getUserOrganizationId();
    if (!organizationId) {
      console.log('❌ No organization ID found');
      return {
        totalAnalyses: 0,
        uniqueCandidates: 0,
        uniqueJobs: 0,
        recentAnalyses: 0,
        totalCandidates: 0,
        totalJobs: 0
      };
    }

    console.log('✅ Organization ID:', organizationId);

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    // Get project-specific candidates and analyses
    const { data: projectCandidates, error: candidatesError } = await supabase
      .from('project_candidates')
      .select(`
        id,
        candidate_id,
        analysis_id,
        created_at,
        analysis!project_candidates_analysis_id_fkey (
          id,
          created_at
        )
      `)
      .eq('project_id', projectId);

    if (candidatesError) {
      console.error('❌ Error fetching project candidates:', candidatesError);
      throw new ApiError('Failed to fetch project candidates', 500);
    }

    console.log('📊 Project candidates found:', projectCandidates?.length || 0);
    console.log('📊 Project candidates data:', projectCandidates);

    // Count unique analyses linked to this project
    const uniqueAnalysisIds = new Set();
    const recentAnalysisIds = new Set();

    (projectCandidates || []).forEach(pc => {
      console.log('Processing project candidate:', {
        id: pc.id,
        candidate_id: pc.candidate_id,
        analysis_id: pc.analysis_id,
        has_analysis: !!pc.analysis,
        analysis_array_length: Array.isArray(pc.analysis) ? pc.analysis.length : 'not array'
      });

      if (pc.analysis_id) {
        uniqueAnalysisIds.add(pc.analysis_id);
        
        // Check if analysis is recent (within last week)
        // The analysis is returned as an array from the join, so we need to access the first element
        const analysisData = Array.isArray(pc.analysis) ? pc.analysis[0] : pc.analysis;
        if (analysisData?.created_at) {
          const analysisDate = new Date(analysisData.created_at);
          if (analysisDate >= oneWeekAgo) {
            recentAnalysisIds.add(pc.analysis_id);
          }
        }
      }
    });

    // Count unique candidates linked to this project
    const uniqueCandidateIds = new Set();
    (projectCandidates || []).forEach(pc => {
      if (pc.candidate_id) {
        uniqueCandidateIds.add(pc.candidate_id);
      }
    });

    console.log('📈 Stats calculated:', {
      uniqueAnalysisIds: Array.from(uniqueAnalysisIds),
      uniqueCandidateIds: Array.from(uniqueCandidateIds),
      totalAnalyses: uniqueAnalysisIds.size,
      totalCandidates: uniqueCandidateIds.size,
      recentAnalyses: recentAnalysisIds.size
    });

    // For project view, jobs count is typically 1 (the project itself represents a job)
    // But we could also count related job descriptions if they exist
    const totalJobs = 1; // The project itself represents one job position

    const result = {
      totalAnalyses: uniqueAnalysisIds.size,
      uniqueCandidates: uniqueCandidateIds.size,
      uniqueJobs: totalJobs,
      recentAnalyses: recentAnalysisIds.size,
      totalCandidates: uniqueCandidateIds.size,
      totalJobs: totalJobs
    };

    console.log('✅ Final project stats result:', result);

    return result;
  } catch (error) {
    console.error('❌ Project dashboard stats error:', error);
    throw new ApiError('Failed to fetch project dashboard stats', 500);
  }
}